/**
 * 매핑의 손 역할 하나 → 3D 가 그릴 pose 시계열과 그리퍼 시계열.
 *
 * 정본은 BE `spawning_pool_templates/mapping/compose.py` 의 `compose_hand` 다. 순서도 같다.
 *
 * 1. 위치 · 자세 채널에서 고른 열을 읽는다.
 * 2. 두 채널이 다르면 시계 · 행 수 · 각 시각이 정확히 같아야 한다(`alignmentProblem`). 보간하지 않는다.
 * 3. 위치에 단위 배율(m 1 · cm 0.01 · mm 0.001)을 곱한다.
 * 4. 자세를 행렬로 해석한다(`orientationMatrix`). 기준 변환이 fixed 면 TCP 보다 먼저 `p = Rb · p + t`,
 *    `R = Rb · R` 로 `reference_frame` 에 옮긴다(BE `tcp_pose._reexpress`). 손 보정이 있으면 `R · C · Rx · Ry · Rz` 다.
 * 5. 그리퍼는 자기 채널의 시각을 그대로 쓴다. 값은 BE 정규화 식과 `clip_range` 를 따른다.
 *
 * BE 와 다른 점은 하나다. BE 는 무효 행 · 비유한 값이 있으면 그 역할 전체를 거부한다. 3D 는 그 행만
 * 그리지 않고 수를 센다. 거부 여부는 검증이 말한다. 자세를 해석할 수 없는 행(비단위 quaternion · 반사
 * 행렬)은 BE 와 같이 역할 전체를 거부한다. 실패한 회전을 항등으로 그리지 않는다.
 *
 * Owns:
 * - 역할 → pose(`[x,y,z,qw,qx,qy,qz]` m · WXYZ, `reference_frame` 값) · 그리퍼 값, 계산할 수 없는 사유와 그 문구.
 *
 * Does not own:
 * - 회전 식 — `rotationMath.ts`. 표본 유효성 · gap — `poseSampling.ts`.
 */
import type { InspectChannel } from "../domain/episodeInspect";
import { alignmentProblem, type ChannelAlignmentReason } from "../domain/inspectChannels";
import {
  GRIPPER_COLUMN_COUNT,
  isIdentityRotation,
  isSelectionComplete,
  orientationColumnCount,
  POSITION_COLUMN_COUNT,
  repeatedColumnIndexes,
  type Selection,
  type Gripper,
  type HandRole,
  type Orientation,
  type LengthUnit,
} from "../domain/mapping";
import type { PoseSeries } from "../domain/viewerTypes";
import type { PoseWxyz } from "./poseMath";
import { addVectors, rotateVector } from "./handKinematics";
import { preparePoseSeries } from "./poseSampling";
import { tcpPoints } from "./tcpPose";
import {
  QUATERNION_MIN_NORM,
  firstSignMixedRow,
  handMatrix,
  matrixToQuaternionWxyz,
  multiplyMatrices,
  orientationMatrix,
  referenceMatrix,
  type Matrix3,
  type OrientationProblem,
  type Vec3,
} from "./rotationMath";

const METERS_PER_UNIT: Readonly<Record<LengthUnit, number>> = { m: 1, cm: 0.01, mm: 0.001 };

export type SelectionProblem =
  "column_repeated" | "incomplete" | "column_count" | "channel_missing" | "column_out_of_range";

type PoseComponent = "position" | "orientation";

/** 고정 기준 변환을 계산하지 못한 이유. BE 의 `reference.*` issue · 저장 검증과 같은 조건이다. */
export type ReferenceTransformProblem = "incomplete" | "same_frame" | "not_unit";

export type RoleTrackProblem =
  | { readonly kind: "position_missing" }
  | { readonly kind: "position_unit_missing" }
  | { readonly kind: "orientation_missing" }
  | {
      readonly kind: "selection";
      readonly component: PoseComponent;
      readonly problem: SelectionProblem;
      readonly channel: string | null;
    }
  | { readonly kind: "orientation_spec"; readonly problem: OrientationProblem }
  | { readonly kind: "alignment"; readonly reason: ChannelAlignmentReason }
  | {
      readonly kind: "orientation_value";
      readonly problem: OrientationProblem;
      readonly row: number;
    }
  | { readonly kind: "hand_axes_parallel" }
  | { readonly kind: "reference_transform"; readonly problem: ReferenceTransformProblem };

export type GripperProblem =
  | { readonly kind: "gripper_missing" }
  | {
      readonly kind: "selection";
      readonly problem: SelectionProblem;
      readonly channel: string | null;
    }
  | { readonly kind: "endpoints_missing" }
  | { readonly kind: "endpoints_equal" };

/** 정규화한 그리퍼 출력. `null` 은 무효 표시 · 비유한 값이라 쓰지 않은 행이다. */
export type GripperSeries = {
  readonly timestampsSec: readonly number[];
  readonly values: readonly (number | null)[];
};

export type GripperResult =
  | { readonly ok: true; readonly series: GripperSeries }
  | { readonly ok: false; readonly problem: GripperProblem };

export type RoleTrack = {
  readonly series: PoseSeries;
  readonly source: PoseSeries;
  /** 무효 표시 · 비유한 값이라 그리지 않은 행 수. BE 검증은 이런 행을 오류로 볼 수 있다. */
  readonly skippedRows: number;
  readonly gripper: GripperResult;
};

export type RoleTrackResult =
  | { readonly ok: true; readonly track: RoleTrack }
  | { readonly ok: false; readonly problem: RoleTrackProblem };

type Selected =
  | {
      readonly ok: true;
      readonly channel: InspectChannel;
      readonly rows: readonly (number[] | null)[];
    }
  | { readonly ok: false; readonly problem: SelectionProblem };

/** 고른 열을 행마다 읽는다. 무효 표시 · 비유한 값의 행은 `null` 이다. */
function selectColumns(
  selection: Selection,
  count: number,
  channels: ReadonlyMap<string, InspectChannel>,
): Selected {
  if (repeatedColumnIndexes(selection.columns).length > 0) {
    return { ok: false, problem: "column_repeated" };
  }
  if (!isSelectionComplete(selection)) {
    return { ok: false, problem: "incomplete" };
  }
  if (selection.columns.length !== count) {
    return { ok: false, problem: "column_count" };
  }
  const channel = channels.get(selection.channel ?? "");
  if (channel === undefined || channel.values === null) {
    return { ok: false, problem: "channel_missing" };
  }
  const values = channel.values;
  const columns = selection.columns as readonly number[];
  const dim = values[0]?.length ?? 0;
  if (values.length > 0 && columns.some((column) => column >= dim)) {
    return { ok: false, problem: "column_out_of_range" };
  }
  const rows = values.map((row, index) => {
    if (!channel.validity[index]) {
      return null;
    }
    const picked = columns.map((column) => row[column]);
    return picked.every(Number.isFinite) ? picked : null;
  });
  return { ok: true, channel, rows };
}

/** 행을 보기 전에 알 수 있는 자세 표현의 빈칸. */
function orientationSpecProblem(spec: Orientation): OrientationProblem | null {
  switch (spec.kind) {
    case null:
      return "kind_missing";
    case "quaternion":
      return spec.quaternion_order === null ? "quaternion_spec" : null;
    case "euler":
      return spec.euler_order === null || spec.euler_frame === null || spec.angle_unit === null
        ? "euler_spec"
        : null;
    case "matrix":
      return null;
  }
}

type ReferenceMove = { readonly rotation: Matrix3; readonly translation: Vec3 };

type ReferenceMoveResult =
  | { readonly ok: true; readonly move: ReferenceMove | null }
  | { readonly ok: false; readonly problem: ReferenceTransformProblem };

/** 고정 기준 변환의 Rb · t. identity 면 옮기지 않는다(`null`). */
function referenceMove(role: HandRole): ReferenceMoveResult {
  const spec = role.reference_transform;
  if (spec.kind === "identity") {
    return { ok: true, move: null };
  }
  const [w, x, y, z] = spec.quaternion_wxyz;
  const { source_frame: sourceFrame, x_m: tx, y_m: ty, z_m: tz } = spec;
  if (
    sourceFrame === null ||
    w === null ||
    x === null ||
    y === null ||
    z === null ||
    tx === null ||
    ty === null ||
    tz === null
  ) {
    return { ok: false, problem: "incomplete" };
  }
  // 이름 앞뒤 공백은 허용하므로 공백만 다른 같은 이름도 같은 프레임으로 본다(BE 와 같다).
  if (sourceFrame.trim() === role.reference_frame?.trim()) {
    return { ok: false, problem: "same_frame" };
  }
  const rotation = referenceMatrix([w, x, y, z]);
  if (rotation === null) {
    return { ok: false, problem: "not_unit" };
  }
  return { ok: true, move: { rotation, translation: [tx, ty, tz] } };
}

/** 역할 하나의 pose 시계열. 계산할 수 없으면 첫 사유를 돌려준다. */
export function buildRoleTrack(
  role: HandRole,
  channels: ReadonlyMap<string, InspectChannel>,
): RoleTrackResult {
  const { position, orientation } = role;
  if (position.source.channel === null) {
    return { ok: false, problem: { kind: "position_missing" } };
  }
  const positionRows = selectColumns(position.source, POSITION_COLUMN_COUNT, channels);
  if (!positionRows.ok) {
    return {
      ok: false,
      problem: {
        kind: "selection",
        component: "position",
        problem: positionRows.problem,
        channel: position.source.channel,
      },
    };
  }
  if (position.unit === null) {
    return { ok: false, problem: { kind: "position_unit_missing" } };
  }
  if (orientation.source.channel === null) {
    return { ok: false, problem: { kind: "orientation_missing" } };
  }
  const specProblem = orientationSpecProblem(orientation);
  if (specProblem !== null) {
    return { ok: false, problem: { kind: "orientation_spec", problem: specProblem } };
  }
  const orientationRows = selectColumns(
    orientation.source,
    orientationColumnCount(orientation.kind),
    channels,
  );
  if (!orientationRows.ok) {
    return {
      ok: false,
      problem: {
        kind: "selection",
        component: "orientation",
        problem: orientationRows.problem,
        channel: orientation.source.channel,
      },
    };
  }
  const alignment = alignmentProblem(positionRows.channel, orientationRows.channel);
  if (alignment !== null) {
    return { ok: false, problem: { kind: "alignment", reason: alignment } };
  }
  // BE 와 같이 항등 보정은 곱하지 않는다.
  let hand: Matrix3 | null = null;
  if (!isIdentityRotation(role.rotation)) {
    hand = handMatrix(role.rotation);
    if (hand === null) {
      return { ok: false, problem: { kind: "hand_axes_parallel" } };
    }
  }
  const reference = referenceMove(role);
  if (!reference.ok) {
    return { ok: false, problem: { kind: "reference_transform", problem: reference.problem } };
  }
  const move = reference.move;
  if (orientation.kind === "quaternion") {
    const row = firstSignMixedRow(orientationRows.rows);
    if (row !== null) {
      return {
        ok: false,
        problem: { kind: "orientation_value", problem: "quaternion_sign_mixed", row },
      };
    }
  }

  const scale = METERS_PER_UNIT[position.unit];
  const poses: (PoseWxyz | null)[] = [];
  const sourcePoses: (PoseWxyz | null)[] = [];
  let skippedRows = 0;
  for (let row = 0; row < positionRows.rows.length; row += 1) {
    const xyz = positionRows.rows[row];
    const angles = orientationRows.rows[row];
    if (xyz === null || angles === null) {
      poses.push(null);
      sourcePoses.push(null);
      skippedRows += 1;
      continue;
    }
    const rotation = orientationMatrix(angles, orientation);
    if (!rotation.ok) {
      return { ok: false, problem: { kind: "orientation_value", problem: rotation.problem, row } };
    }
    const scaled: Vec3 = [xyz[0] * scale, xyz[1] * scale, xyz[2] * scale];
    // S · Rs 는 기준 변환 뒤 값이다. 뒤의 TCP 이동 d · 손 보정 H 는 원본 도구 축 그대로다.
    const sourcePosition =
      move === null ? scaled : addVectors(rotateVector(move.rotation, scaled), move.translation);
    const sourceRotation =
      move === null ? rotation.matrix : multiplyMatrices(move.rotation, rotation.matrix);
    const source: PoseWxyz = [...sourcePosition, ...matrixToQuaternionWxyz(sourceRotation)];
    sourcePoses.push(source);
    const points = tcpPoints(sourcePosition, sourceRotation, role, null);
    poses.push(points.handPose ?? source);
  }
  return {
    ok: true,
    track: {
      series: preparePoseSeries(positionRows.channel.secondsFromOrigin, poses),
      source: preparePoseSeries(positionRows.channel.secondsFromOrigin, sourcePoses),
      skippedRows,
      gripper: buildGripperSeries(role.gripper, channels),
    },
  };
}

/**
 * 그리퍼 정규화 출력. 출력은 BE 계약대로 닫힘 1 · 열림 0 으로 고정이고, BE 식 `(x − in_open) / (in_closed − in_open)`
 * 과 같다. 열림 끝에서 −0 이 나오지 않게 `1 − (x − in_closed) / (in_open − in_closed)` 로 쓴다.
 * `clip_range` 의 두 끝이 모두 있으면 그 범위로 자른다. 기본 clip 은 없다.
 */
export function buildGripperSeries(
  gripper: Gripper,
  channels: ReadonlyMap<string, InspectChannel>,
): GripperResult {
  if (gripper.source.channel === null) {
    return { ok: false, problem: { kind: "gripper_missing" } };
  }
  const selected = selectColumns(gripper.source, GRIPPER_COLUMN_COUNT, channels);
  if (!selected.ok) {
    return {
      ok: false,
      problem: { kind: "selection", problem: selected.problem, channel: gripper.source.channel },
    };
  }
  const { input_closed: inputClosed, input_open: inputOpen } = gripper;
  if (inputClosed === null || inputOpen === null) {
    return { ok: false, problem: { kind: "endpoints_missing" } };
  }
  if (inputOpen === inputClosed) {
    return { ok: false, problem: { kind: "endpoints_equal" } };
  }
  const [clipLow, clipHigh] = gripper.clip_range;
  const values = selected.rows.map((row) => {
    if (row === null) {
      return null;
    }
    const value = 1 - (row[0] - inputClosed) / (inputOpen - inputClosed);
    if (clipLow === null || clipHigh === null) {
      return value;
    }
    // numpy `clip(x, lo, hi)` 와 같은 식이다.
    return Math.min(Math.max(value, clipLow), clipHigh);
  });
  return { ok: true, series: { timestampsSec: selected.channel.secondsFromOrigin, values } };
}

const COMPONENT_NAME: Readonly<Record<PoseComponent, string>> = {
  position: "위치",
  orientation: "자세",
};

function selectionText(
  component: string,
  problem: SelectionProblem,
  channel: string | null,
): string {
  switch (problem) {
    case "column_repeated":
      return `${component} 에 같은 열을 두 번 골랐습니다. 서로 다른 열을 골라 주세요.`;
    case "incomplete":
      return `${component} 채널과 열을 모두 고르지 않았습니다.`;
    case "column_count":
      return `${component} 열 수가 표현과 맞지 않습니다. 열을 다시 골라 주세요.`;
    case "channel_missing":
      return `${component} 채널 ${channel ?? ""} 이 이 episode 에 없습니다.`;
    case "column_out_of_range":
      return `${component} 열 번호가 채널 ${channel ?? ""} 의 차원 수를 넘습니다.`;
  }
}

const ALIGNMENT_TEXT: Readonly<Record<ChannelAlignmentReason, string>> = {
  clock_unknown: "위치 채널이나 자세 채널의 시계를 확인할 수 없습니다. 한 자세로 합치지 않습니다.",
  clock_mismatch: "위치 채널과 자세 채널이 서로 다른 시계를 씁니다. 한 자세로 합치지 않습니다.",
  row_count_mismatch: "위치 채널과 자세 채널의 행 수가 다릅니다. 한 자세로 합치지 않습니다.",
  timestamp_mismatch:
    "위치 채널과 자세 채널의 기록 시각이 다릅니다. 보간하지 않으므로 한 자세로 합치지 않습니다.",
};

function orientationProblemText(problem: OrientationProblem, row: number | null): string {
  const where = row === null ? "" : `행 ${row} 의 `;
  switch (problem) {
    case "kind_missing":
      return "자세 표현(quaternion · Euler · 행렬)을 고르지 않았습니다.";
    case "quaternion_spec":
      return "quaternion 성분 순서를 고르지 않았습니다.";
    case "euler_spec":
      return "Euler 축 순서 · 축 기준 · 각 단위를 모두 고르지 않았습니다.";
    case "matrix_spec":
      return "회전 행렬은 행 우선 9 열이어야 합니다.";
    case "non_finite":
      return `${where}자세 값이 유한한 수가 아닙니다.`;
    case "quaternion_zero":
      return `${where}quaternion 길이가 0 입니다.`;
    case "quaternion_sign_mixed":
      return `${where}quaternion 길이가 ${QUATERNION_MIN_NORM} 보다 짧습니다. 원본이 부호가 다른 quaternion 을 섞어 평균한 값이라 정규화해도 자세가 돌아오지 않습니다. 같은 손의 다른 자세 채널을 고르거나 Dataset 조사 사실을 확인해 주세요.`;
    case "quaternion_not_unit":
      return `${where}quaternion 이 단위 길이가 아닙니다. 정규화를 켜거나 열과 순서를 확인해 주세요.`;
    case "matrix_not_rotation":
      return `${where}행렬이 회전 행렬이 아닙니다. 직교이고 det 가 +1 이어야 합니다.`;
  }
}

const REFERENCE_TRANSFORM_TEXT: Readonly<Record<ReferenceTransformProblem, string>> = {
  incomplete:
    "기준 변환을 고정으로 골랐지만 원본 좌표계 · quaternion · 이동 칸을 모두 채우지 않았습니다.",
  same_frame:
    "기준 변환의 원본 좌표계가 기준 좌표계와 같습니다. 기준 프레임에는 출력 좌표계를 적습니다. 옮기지 않는다면 기준 변환을 없음으로 두어 주세요.",
  not_unit:
    "기준 변환 quaternion 의 길이가 1 이 아닙니다. 조용히 정규화하지 않으니 네 성분을 확인해 주세요.",
};

/** 3D 가 그 손을 그리지 못한 이유 한 문장. */
export function roleTrackProblemText(problem: RoleTrackProblem): string {
  switch (problem.kind) {
    case "position_missing":
      return "위치 채널을 고르지 않았습니다.";
    case "position_unit_missing":
      return "위치 단위를 고르지 않았습니다.";
    case "orientation_missing":
      return "자세 채널을 고르지 않았습니다. 자세 없이 손을 항등 자세로 그리지 않습니다.";
    case "selection":
      return selectionText(COMPONENT_NAME[problem.component], problem.problem, problem.channel);
    case "orientation_spec":
      return orientationProblemText(problem.problem, null);
    case "alignment":
      return ALIGNMENT_TEXT[problem.reason];
    case "orientation_value":
      return orientationProblemText(problem.problem, problem.row);
    case "hand_axes_parallel":
      return "손 X 축과 Y 축이 직교하지 않습니다.";
    case "reference_transform":
      return REFERENCE_TRANSFORM_TEXT[problem.problem];
  }
}

/** 그리퍼로 손가락을 움직이지 못한 이유 한 문장. 이때 손가락은 쉬는 자세(열림, `RESTING_OPENING`)로 둔다. */
export function gripperProblemText(problem: GripperProblem): string {
  const resting = " 손가락은 열린 채로 둡니다.";
  switch (problem.kind) {
    case "gripper_missing":
      return `그리퍼 채널을 고르지 않았습니다.${resting}`;
    case "selection":
      return `${selectionText("그리퍼", problem.problem, problem.channel)}${resting}`;
    case "endpoints_missing":
      return `그리퍼 입력 끝점 두 개를 모두 적지 않았습니다.${resting}`;
    case "endpoints_equal":
      return `그리퍼 입력의 닫힘 값과 열림 값이 같습니다.${resting}`;
  }
}
